import { STRAIGHT_LENGTH, CURVE_RADIUS, TRACK_LENGTH } from './track.js';
import { FLOW_STORIES } from './flow-stories.js';

// Role 0 is the eventual winner; roles 1-4 are independently assigned rivals.
// The eight orders cover launch, approach, braking, apex, exit, chase, sprint,
// and the line. Identical adjacent orders deliberately allow a battle to settle.
const definitions = [
  ['recovery', 32, '12340 12304 12034 21034 20134 02134 02134 02134', [1, 1.1, 1, .9, .8, .85, 1.1, 1.2], [0, -7, -12, -18, -25], 1.6],
  ['counterattack', 32, '10234 01234 01234 10234 12034 10234 01234 01234', [.8, 1, .85, .7, .65, .8, .65, .8], [0, -1.2, -9, -16, -23], 1.6],
  ['braking', 30, '21304 21304 02134 02134 20134 02134 02134 02134', [1, 1.1, .75, .95, .6, .9, 1.1, 1.1], [0, -5, -11, -16, -23], 1.6],
  ['corner-duel', 34, '12034 12034 10234 01234 10234 01234 10234 01234', [.85, .8, .7, .55, .6, .6, .45, .45], [0, -.45, -8, -14, -21], 1.45],
  ['breakthrough', 32, '12304 21304 21034 12034 12034 01234 01234 01234', [.9, .9, .65, .6, .5, .6, 1, 1.1], [0, -5.5, -8, -17, -25], 1.6],
  ['sprint-duel', 32, '12034 10234 01234 10234 10234 01234 10234 01234', [1, .85, .95, .8, .9, .65, .6, .55], [0, -.7, -10, -15, -23], 1.1],
  ['comeback', 34, '12340 21340 21304 21034 20134 02134 02134 02134', [1.2, 1.2, 1.05, .9, .8, .8, 1, 1.1], [0, -4, -11, -19, -27], 1.6],
  ['three-way', 34, '23104 21304 21034 12034 10234 12034 12034 01234', [1, .9, .8, .65, .6, .45, .3, .3], [0, -.16, -.42, -12, -20], .75],
  ['lead-relay', 34, '31204 13204 21304 12304 31204 10234 21034 02134', [.9, .85, .8, .8, .75, .7, .55, .7], [0, -2, -6, -13, -21], 1.5],
  ['photo-finish', 30, '21304 12304 12034 21034 20134 20134 20134 02134', [1, 1, .9, .75, .65, .65, .4, .4], [0, -.18, -8, -15, -23], .28],
];

export const SCENARIOS = Object.freeze(definitions.map(([family, duration, orders, spreads, finishOffsets, finishWindow], index) => {
  const cornerExit = 17.5 + (duration - 30) * .65;
  const exitSpeed = 24 - (duration - 30) * 2;
  return Object.freeze({
    id: `s${String(index + 1).padStart(2, '0')}`, family, duration, cornerExit, finishWindow,
    orders: Object.freeze(orders.split(' ').map(order => Object.freeze([...order].map(Number)))),
    spreads: Object.freeze(spreads), finishOffsets: Object.freeze(finishOffsets),
    beatTimes: Object.freeze([4, 6.4, 9, 9 + (cornerExit - 9) * .46, cornerExit,
      cornerExit + (duration - cornerExit) * .48, duration - finishWindow, duration - .02]),
    motionKeys: Object.freeze([
      [0, 0, 0], [4, 65, 32], [6.5, 185, 54], [9, STRAIGHT_LENGTH, 24],
      [cornerExit, STRAIGHT_LENGTH + Math.PI * CURVE_RADIUS, exitSpeed], [duration, TRACK_LENGTH, 60],
    ].map(key => Object.freeze(key))),
  });
}));

export function storyIndexFromSearch(search) {
  const id = new URLSearchParams(search).get('story');
  if (!id) return undefined;
  const flow = FLOW_STORIES.findIndex(story => story.id === id);
  if (flow >= 0) return flow;
  const index = SCENARIOS.findIndex(story => story.id === id);
  return index < 0 ? undefined : index;
}

// What repeats in the memory is the shape of a race rather than its exact
// number, so the families of the last few rounds are held out along with their
// stories. Three families plus the last ten stories still leaves at least 25
// candidates, so the draw cannot run dry.
const FAMILY_COOLDOWN = 3;

export function drawStoryIndex(recentScenarios, random) {
  const recent = recentScenarios.filter(index =>
    Number.isInteger(index) && index >= 0 && index < FLOW_STORIES.length);
  const cooled = new Set(recent.slice(-FAMILY_COOLDOWN).map(index => FLOW_STORIES[index].family));
  const available = FLOW_STORIES.map((_, index) => index)
    .filter(index => !recent.includes(index) && !cooled.has(FLOW_STORIES[index].family));
  const pool = available.length ? available
    : FLOW_STORIES.map((_, index) => index).filter(index => !recent.includes(index));
  return pool[random() % pool.length];
}
